import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertTriangle } from 'lucide-react';
import api from '../../services/api';

const FilePreviewModal = ({ fileUid, label, onClose }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [contentType, setContentType] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const blobUrlRef = useRef(null);

    useEffect(() => {
        if (!fileUid) return;

        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setError('');
            try {
                const { blob, contentType: ct } = await api.getFile(fileUid);
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                blobUrlRef.current = url;
                setContentType(ct);
                setBlobUrl(url);
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to load file.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [fileUid]);

    useEffect(() => {
        const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) onClose();
    };

    const isPdf = contentType?.includes('pdf');
    const isImage = contentType?.startsWith('image/');
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);

    return createPortal(
        <div
            className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/85 backdrop-blur-lg animate-fade-in"
            onClick={handleBackdropClick}
        >
            <div className="w-full max-w-4xl flex flex-col max-h-[90vh] bg-surface-raised border border-surface-border/60 rounded-lg shadow-glow-lg animate-appear overflow-hidden">
                {/* Accent strip */}
                <div className="h-[2px] bg-gradient-to-r from-transparent via-accent/60 to-transparent shrink-0" />

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 shrink-0">
                    <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide truncate">
                        {label || 'File Preview'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 text-text-muted hover:text-accent rounded transition-colors cursor-pointer flex-shrink-0 ml-3"
                        aria-label="Close preview"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="h-px bg-surface-border/50 mx-5 shrink-0" />

                {/* Content */}
                <div className="flex-1 min-h-0 flex items-center justify-center">
                    {isLoading && (
                        <div className="flex flex-col items-center gap-2 py-16 text-text-muted">
                            <Loader className="w-6 h-6 animate-spin text-accent" />
                            <span className="text-xs">Loading file…</span>
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="flex flex-col items-center gap-2 py-16 text-status-overdue">
                            <AlertTriangle className="w-6 h-6" />
                            <span className="text-sm">{error}</span>
                        </div>
                    )}

                    {!isLoading && !error && blobUrl && isPdf && !isMobile && (
                        <iframe
                            src={blobUrl}
                            className="w-full h-full min-h-[70vh]"
                            title={label || 'File Preview'}
                        />
                    )}

                    {!isLoading && !error && blobUrl && isPdf && isMobile && (
                        <div className="flex flex-col items-center gap-4 py-16 text-text-muted">
                            <span className="text-sm text-center px-4">Inline PDF preview is not supported on this device.</span>
                            <a
                                href={blobUrl}
                                download={label || 'file.pdf'}
                                className="px-4 py-2 bg-accent text-white text-sm font-medium rounded hover:opacity-90 transition-opacity"
                            >
                                Download PDF
                            </a>
                        </div>
                    )}

                    {!isLoading && !error && blobUrl && isImage && (
                        <div className="overflow-auto max-h-[70vh] p-4 flex items-center justify-center">
                            <img
                                src={blobUrl}
                                alt={label || 'File Preview'}
                                className="max-w-full max-h-[65vh] object-contain rounded"
                            />
                        </div>
                    )}

                    {!isLoading && !error && blobUrl && !isPdf && !isImage && (
                        <div className="flex flex-col items-center gap-2 py-16 text-text-muted">
                            <span className="text-sm">Preview not available for this file type.</span>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default FilePreviewModal;
